import React from 'react';

interface RoundRobinCycleDiagramProps {
  size?: number;
  showLabels?: boolean;
}

/**
 * Visual representation of the 8-year Round Robin rotation cycle (DARK THEME)
 * Shows how positions advance 201 positions per year in a clockwise direction
 */
const RoundRobinCycleDiagram: React.FC<RoundRobinCycleDiagramProps> = ({ 
  size = 300,
  showLabels = true 
}) => {
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = (size / 2) - 40;

  // 8-year cycle positions - using vibrant colors for dark backgrounds
  const years = [
    { year: 'Year 1', position: 1, color: '#10b981', angle: 0 },      // green
    { year: 'Year 2', position: 202, color: '#3b82f6', angle: 45 },   // blue
    { year: 'Year 3', position: 403, color: '#a855f7', angle: 90 },   // purple
    { year: 'Year 4', position: 604, color: '#ec4899', angle: 135 },  // pink
    { year: 'Year 5', position: 805, color: '#ef4444', angle: 180 },  // red
    { year: 'Year 6', position: 1006, color: '#f97316', angle: 225 }, // orange
    { year: 'Year 7', position: 1207, color: '#eab308', angle: 270 }, // yellow
    { year: 'Year 8', position: 1408, color: '#22c55e', angle: 315 }, // lime
  ];

  const getCoordinates = (angle: number) => {
    const radian = (angle - 90) * (Math.PI / 180);
    return {
      x: centerX + radius * Math.cos(radian),
      y: centerY + radius * Math.sin(radian)
    };
  };

  return (
    <div style={{ 
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      justifyContent: 'center',
      flexWrap: 'wrap'
    }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke="#4b5563"
          strokeWidth="2"
          strokeDasharray="5,5"
        />

        {/* Center text */}
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          fontSize="14"
          fontWeight="bold"
          fill="#d1d5db"
        >
          8-Year
        </text>
        <text
          x={centerX}
          y={centerY + 10}
          textAnchor="middle"
          fontSize="14"
          fontWeight="bold"
          fill="#d1d5db"
        >
          Cycle
        </text>

        {/* Year positions */}
        {years.map((yearData, index) => {
          const coords = getCoordinates(yearData.angle);
          const nextIndex = (index + 1) % years.length;
          const nextCoords = getCoordinates(years[nextIndex].angle);

          return (
            <g key={yearData.year}>
              {/* Arrow showing progression */}
              <defs>
                <marker
                  id={`arrow-${index}`}
                  markerWidth="10"
                  markerHeight="10"
                  refX="9"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M0,0 L0,6 L9,3 z" fill={yearData.color} />
                </marker>
              </defs>

              <line
                x1={coords.x}
                y1={coords.y}
                x2={nextCoords.x}
                y2={nextCoords.y}
                stroke={yearData.color}
                strokeWidth="2"
                markerEnd={`url(#arrow-${index})`}
                opacity="0.6"
              />

              {/* Position circle */}
              <circle
                cx={coords.x}
                cy={coords.y}
                r="20"
                fill={yearData.color}
                stroke="#1f2937"
                strokeWidth="2"
              />

              {/* Position number */}
              <text
                x={coords.x}
                y={coords.y + 5}
                textAnchor="middle"
                fontSize="12"
                fontWeight="bold"
                fill="white"
              >
                #{yearData.position}
              </text>

              {/* Year label */}
              {showLabels && (
                <text
                  x={coords.x}
                  y={coords.y < centerY ? coords.y - 30 : coords.y + 40}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="600"
                  fill="#9ca3af"
                >
                  {yearData.year}
                </text>
              )}
            </g>
          );
        })}

        {/* Clockwise rotation indicator */}
        <g>
          <path
            d={`M ${centerX + radius * 0.3} ${centerY - radius * 0.5} 
                Q ${centerX + radius * 0.5} ${centerY - radius * 0.3} 
                ${centerX + radius * 0.5} ${centerY - radius * 0.1}`}
            fill="none"
            stroke="#6b7280"
            strokeWidth="2"
            markerEnd="url(#rotation-arrow)"
          />
          <defs>
            <marker
              id="rotation-arrow"
              markerWidth="8"
              markerHeight="8"
              refX="7"
              refY="3"
              orient="auto"
            >
              <path d="M0,0 L0,6 L8,3 z" fill="#6b7280" />
            </marker>
          </defs>
          <text
            x={centerX + radius * 0.4}
            y={centerY - radius * 0.6}
            textAnchor="middle"
            fontSize="10"
            fill="#9ca3af"
            fontWeight="600"
          >
            Clockwise
          </text>
        </g>
      </svg>

      {/* Legend - Now positioned to the side */}
      {showLabels && (
        <div style={{
          maxWidth: '280px',
          padding: '16px',
          backgroundColor: '#1f2937',
          borderRadius: '8px',
          border: '1px solid #374151',
          alignSelf: 'center'
        }}>
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: '#9ca3af',
            lineHeight: '1.6'
          }}>
            <strong style={{ color: '#d1d5db', display: 'block', marginBottom: '8px' }}>Example:</strong>
            RR #1 starts at Position #1, advances to #202 in Year 2, 
            reaches #805 by Year 5, and returns to Position #9 after completing the full 8-year cycle.
          </p>
        </div>
      )}
    </div>
  );
};

export default RoundRobinCycleDiagram;

